import React from 'react';
import { Form, Formik } from 'formik';
import { useMutation } from '@apollo/client';
import FormikTextArea from '@minecraft.formik-textarea';
import { useGlobalNotification } from '@minecraft.notification-provider';
import Modal from '@minecraft.modal';
import { BodyText, FlexCol, FlexRow } from '@minecraft.atoms';
import { TalentScoutContactTalentDoc, TalentScoutProfileFragment } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';

const ContactTalentForm: React.FC<{ stageName: string }> = ({ stageName }) => {
  const { t } = useTranslation();

  return (
    <Form>
      <FlexCol gap="5">
        <BodyText>{t('reps:talentScout.contact.talentCanRespond')}</BodyText>
        <FlexRow gap="4">
          <BodyText as="div" fontWeight="bold">
            {t('common:label.To')}:
          </BodyText>
          <BodyText as="div">{stageName}</BodyText>
        </FlexRow>
        <FormikTextArea
          className="cn_w-full cn_mt-1"
          data-testid="talent-scout-search-profile-contact-modal-message"
          label={t('reps:talentScout.contact.yourMessage')}
          maxLength={2000}
          name="messageToTalent"
          required
          rows={5}
          showLabel
          style={{ resize: 'none' }}
        />
      </FlexCol>
    </Form>
  );
};

export const ContactTalentModal: React.FC<
  Pick<TalentScoutProfileFragment, 'profileId' | 'stageName'> & {
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
  }
> = ({ profileId, stageName, showModal, setShowModal }) => {
  const closeModal = React.useCallback(() => setShowModal(false), [setShowModal]);
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const [contactTalent, { loading }] = useMutation(TalentScoutContactTalentDoc, {
    onError: () => {
      closeModal();
      showMessage({ color: 'danger', message: t('reps:talentScout.contact.messageSendFailure') });
    },
    onCompleted: (data) => {
      if (data) {
        closeModal();
        showMessage({ message: t('reps:talentScout.contact.messageSendSuccess') });
      }
    },
  });
  const submit = React.useCallback(
    async (message: string) => {
      if (profileId && !Number.isNaN(Number(profileId)) && message) {
        await contactTalent({
          variables: {
            input: {
              message,
              profileId: Number(profileId),
            },
          },
        });
        closeModal();
      }
    },
    [profileId, contactTalent, closeModal]
  );

  return (
    <Formik<{ messageToTalent: string }>
      initialValues={{ messageToTalent: '' }}
      onSubmit={async ({ messageToTalent }, { resetForm }) => {
        await submit(messageToTalent);
        resetForm();
      }}
    >
      {({ values, handleSubmit, resetForm }) => (
        <Modal
          cancelLabel={t('common:button.cancel')}
          data-qa-id="talent-scout-search-profile-contact-modal"
          data-testid="talent-scout-search-profile-contact-modal"
          handleCancelClick={() => {
            closeModal();
            resetForm();
          }}
          handleSubmitClick={() => handleSubmit()}
          handleToggleClick={() => {
            closeModal();
            resetForm();
          }}
          isOpen={showModal}
          modalSize="small"
          overlayLoading={loading}
          submitDisabled={loading || !values.messageToTalent.trim()}
          submitLabel={t('common:button.send')}
          title={t('reps:talentScout.contact.contactFromTalentScout')}
        >
          <ContactTalentForm stageName={stageName} />
        </Modal>
      )}
    </Formik>
  );
};
